import '@types'
import { useExperimentsStore } from '@shared/stores/experiments.js'
import { toCamelCase, toPascalCase } from '@/utils/stringParsing.js' // TODO: ENG-3647 - Isolate shared code.
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useClientStore } from '@shared/stores/client.js'
import { detectRecursion } from '@shared/utils/fn.js'

/**
 * Registry for experiment composables.
 * Keys can be strings or regex patterns.
 * @type {Map<string | RegExp, function(): ReturnType<useExperiment>>}
 */
export const experimentComposableRegistry = new Map()
function getRegisteredComposable (name) {
  const composable = experimentComposableRegistry.get(name)
  if (composable) return composable

  for (const [key, value] of experimentComposableRegistry) {
    if (key instanceof RegExp && key.test(name)) {
      return value
    }
  }
  return null
}

/**
 * @param {string} name
 * @param {ExperimentOptions} options
 * @param {boolean} skipRegistry Skip the registry check.
 * @returns {Experiment}
 */
export default function useExperiment (name, options = {}, skipRegistry = false) {
  if (!skipRegistry) {
    detectRecursion()
    const registeredComposable = getRegisteredComposable(name)
    if (registeredComposable) {
      return registeredComposable()
    }
  }

  const experimentsStore = useExperimentsStore()
  const { updateMetadata, registerForExperiment, fetchRegisteredExperiments } = experimentsStore
  const { experiments, experimentsLoaded } = storeToRefs(experimentsStore)
  const { isAuthenticated } = storeToRefs(useClientStore())

  updateMetadata(name, options)

  const experiment = computed(() => experiments.value[name])
  const showOriginalWhenInactive = computed(() => experiment.value?.showOriginalWhenInactive === true)
  const requireClientCreatedEvent = computed(() => experiment.value?.requireClientCreatedEvent === true)
  const clientCreatedCriteriaMet = computed(() =>
    !requireClientCreatedEvent.value ||
    !isAuthenticated.value ||
    clientCreatedEvent.value)
  const isActive = computed(() => experiment.value?.isActive === true)
  const isRegistered = computed(() => experiment.value?.isRegistered === true)
  const events = computed(() => (experiment.value?.events ?? []).reduce((acc, event) => {
    acc[toCamelCase(event)] = true
    return acc
  }, {}))
  const clientCreatedEvent = computed(() => events.value.clientCreated === true)
  const variantNames = computed(() => experiment.value?.variantNames ?? [])
  const variant = computed(() => experiment.value?.variantName)
  const isOriginal = computed(() => variant.value === 'original')
  /**
   * @type {ComputedRef<{ [k: string]: boolean }>}
   */
  const variants = computed(() => variantNames.value.reduce((acc, variantName) => {
    acc[variantName] = variant.value === variantName
    return acc
  }, {}))
  const showOriginal = computed(() =>
    experimentsLoaded.value &&
    !showAnyVariant.value &&
    (isActive.value
      ? isOriginal.value || showOriginalWhenInactive.value
      : showOriginalWhenInactive.value)
  )

  // Generate `isVariant{Name}` computed properties.
  const isVariant = variantNames.value.reduce((acc, variantName) => {
    acc[`isVariant${toPascalCase(variantName)}`] = computed(() => variants.value[variantName] === true)
    return acc
  }, {})

  // Generate `showVariant{Name}` computed properties.
  const showVariant = variantNames.value.reduce((acc, variantName) => {
    acc[`showVariant${toPascalCase(variantName)}`] = computed(() =>
      experimentsLoaded.value &&
      isActive.value &&
      clientCreatedCriteriaMet.value &&
      variants.value[variantName] === true)
    return acc
  }, {})
  const showAnyVariant = computed(() => Object.values(showVariant).some(v => v.value))

  async function register () {
    await registerForExperiment(name)
  }

  if (!experimentsLoaded.value) {
    fetchRegisteredExperiments()
  }

  return {
    name,
    variantNames,
    showOriginalWhenInactive,
    isActive,
    isRegistered,
    events,
    clientCreatedEvent,
    variant,
    variants,
    isOriginal,
    ...isVariant,
    ...showVariant,
    showAnyVariant,
    showOriginal,
    register
  }
}
export function useCancelFlowRedesignExperiment () {
  return useExperiment('cancel_flow_redesign', {
    variantNames: ['redesign'],
    showOriginalWhenInactive: true,
  }, true)
}
experimentComposableRegistry.set(/cancel_flow_redesign/, useCancelFlowRedesignExperiment)

export function useConversationalSearchExperiment () {
  return useExperiment('conversational_search', {
    variantNames: ['chatbot'],
    showOriginalWhenInactive: true,
  }, true)
}
experimentComposableRegistry.set(/conversational_search/, useConversationalSearchExperiment)

export function useHomepageVideoTestQ42025Experiment () {
  return useExperiment('homepage_video_test_q4_2025', {
    variantNames: ['video'],
    showOriginalWhenInactive: true,
  }, true)
}
experimentComposableRegistry.set(/homepage_video_test_q4_2025/, useHomepageVideoTestQ42025Experiment)

export function useSignUpExperiments () {
  function useSignUpExperiment (phase, variantNames = ['redesign']) {
    return useExperiment(`sign_up_phase${phase}`, {
      variantNames,
      showOriginalWhenInactive: true
    }, true)
  }

  const phase3 = useSignUpExperiment('3_4', ['old', 'new'])
  const signUpPhase3 = computed(() => phase3.showVariantNew.value)

  const routeReplacements = computed(() => {
    // <route-name>: <replacement component route name>
    return {
      'style-quiz-create-account': 'sign-up-create-account'
    }
  })

  return {
    signUpPhase3,
    routeReplacements
  }
}
experimentComposableRegistry.set(/sign_up_/, useSignUpExperiments)

export function useQuarterlyRentalsExperiment () {
  return useExperiment('quarterly_rentals', {
    variantNames: ['enabled'],
    showOriginalWhenInactive: false,
  }, true)
}
experimentComposableRegistry.set(/quarterly_rentals/, useQuarterlyRentalsExperiment)
